import "./catalogue.css";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { IoClose, IoSearchOutline } from "react-icons/io5";
import { FiChevronDown, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { motion, useReducedMotion } from "framer-motion";
import useFetchCategories from "api/CategoryResource/useFetchCategories";
import useFetchPublishers from "api/PublisherResource/useFetchPublishers";
import useGenres from "api/GenreResource/useGenres";
import { usePagedTags } from "api/TagResource/useTags";
import { countries } from "utils/data";
import {
	AVAILABILITY_OPTIONS,
	FORMAT_OPTIONS,
	PRICING_OPTIONS,
	appliedBooksPath,
	booksResultsPathFromSearch,
	countActiveFilters,
	draftFromSearch,
	emptyDraft,
} from "./booksQueryParams";

const rangeWarning = (min, max, label) => {
	if (min === "" || max === "" || Number(min) <= Number(max)) return null;
	return `${label} minimum is higher than the maximum.`;
};

const dateWarning = (from, to) => {
	if (!from || !to || from <= to) return null;
	return "The start date is after the end date.";
};

const withoutPageAndLanguage = (draft) => ({
	...draft,
	pageCountMin: "",
	pageCountMax: "",
	language: "",
});

const stackedEntrance = (prefersReducedMotion, delay, distance = 12) => ({
	initial: prefersReducedMotion ? false : { opacity: 0, y: distance },
	animate: { opacity: 1, y: 0 },
	transition: prefersReducedMotion
		? { duration: 0 }
		: {
				duration: 0.18,
				delay: 0,
				ease: [0.22, 1, 0.36, 1],
			},
});

const SearchGlyph = ({ className = "left-4", size = 18 }) => (
	<span className={`pointer-events-none absolute inset-y-0 flex items-center ${className}`}>
		<IoSearchOutline className="block text-gray-400" size={size} aria-hidden="true" />
	</span>
);

const SheetSelect = memo(({ id, label, value, fieldKey, onChange, options, placeholder }) => (
	<div className="catalogue-sheet-select relative min-w-0 px-1">
		<label htmlFor={id} className="catalogue-field-label">
			{label}
		</label>
		<select
			id={id}
			value={value || ""}
			onChange={(event) => onChange(fieldKey, event.target.value)}
			className={`h-12 w-full appearance-none bg-transparent pl-3 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-akgreen ${
				value ? "font-medium text-akgreenest" : "text-gray-800"
			}`}
		>
			<option value="">{placeholder}</option>
			{options.map((option) => (
				<option key={option.value} value={option.value}>
					{option.label}
				</option>
			))}
		</select>
		<span className="pointer-events-none absolute right-2 bottom-3 flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 text-gray-500">
			<FiChevronDown size={14} />
		</span>
	</div>
));

const PillInput = memo(({ id, label, fieldKey, onChange, ...inputProps }) => (
	<div>
		<label htmlFor={id} className="catalogue-field-label">
			{label}
		</label>
		<input
			id={id}
			{...inputProps}
			onChange={(event) => onChange(fieldKey, event.target.value)}
			className="h-12 w-full rounded-full border border-transparent bg-gray-100 px-4 text-center text-sm text-gray-800 placeholder-gray-400 focus:border-akgreen focus:outline-none focus:ring-2 focus:ring-akgreen"
		/>
	</div>
));

const PillChoices = memo(
	({
		legend,
		items,
		selected,
		onToggle,
		searchLabel,
		emptyMessage,
		noMatchMessage,
		loadingMessage = "Loading…",
		remote = false,
		onSearchChange,
		hasMore = false,
		onLoadMore,
		isLoadingMore = false,
		totalCount,
	}) => {
		const scrollerRef = useRef(null);
		const scrollRafRef = useRef(null);
		const prevLengthRef = useRef(0);
		const [query, setQuery] = useState("");
		const [canScrollLeft, setCanScrollLeft] = useState(false);
		const [canScrollRight, setCanScrollRight] = useState(false);

		const visible = useMemo(() => {
			const known = new Set(items.map((item) => item.name));
			const selectedOnly = selected
				.filter((name) => name && !known.has(name))
				.map((name) => ({ id: `selected-${name}`, name }));
			const q = query.trim().toLowerCase();
			const matching =
				remote || !q ? items : items.filter((item) => item.name?.toLowerCase().includes(q));
			return [...selectedOnly, ...matching];
		}, [items, query, remote, selected]);

		const isNearEnd = useCallback((el) => {
			if (!el) return false;
			const lead = Math.max(el.clientWidth * 0.35, 80);
			return el.scrollLeft + el.clientWidth >= el.scrollWidth - lead;
		}, []);

		const maybeLoadMore = useCallback(() => {
			if (!hasMore || isLoadingMore) return;
			const el = scrollerRef.current;
			if (!el) return;
			// Load the next page when the row is already at / near its end, or when
			// every loaded pill fits on screen (nothing left to scroll into).
			if (isNearEnd(el) || el.scrollWidth <= el.clientWidth + 4) {
				onLoadMore?.();
			}
		}, [hasMore, isLoadingMore, isNearEnd, onLoadMore]);

		const updateScrollState = useCallback(() => {
			if (scrollRafRef.current) return;
			scrollRafRef.current = requestAnimationFrame(() => {
				scrollRafRef.current = null;
				const el = scrollerRef.current;
				if (!el) return;
				setCanScrollLeft(el.scrollLeft > 4);
				setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
				maybeLoadMore();
			});
		}, [maybeLoadMore]);

		useEffect(() => {
			const el = scrollerRef.current;
			if (!el) {
				setCanScrollLeft(false);
				setCanScrollRight(false);
				return undefined;
			}

			updateScrollState();

			// jsdom (tests) and some older browsers lack ResizeObserver.
			const canObserve = typeof ResizeObserver !== "undefined";
			const observer = canObserve ? new ResizeObserver(updateScrollState) : null;
			if (observer) {
				observer.observe(el);
			} else {
				window.addEventListener("resize", updateScrollState);
			}

			return () => {
				if (observer) {
					observer.disconnect();
				} else {
					window.removeEventListener("resize", updateScrollState);
				}
				if (scrollRafRef.current) {
					cancelAnimationFrame(scrollRafRef.current);
					scrollRafRef.current = null;
				}
			};
		}, [visible.length, hasMore, isLoadingMore, updateScrollState]);

		// Keep the first paint at the start of the row. Only nudge forward when
		// more pills were appended while the user had already scrolled near the end.
		useEffect(() => {
			const el = scrollerRef.current;
			const previous = prevLengthRef.current;
			const grew = items.length > previous;
			const reset = items.length === 0 || previous === 0 || items.length < previous;

			if (el && reset) {
				el.scrollLeft = 0;
				updateScrollState();
			} else if (el && grew && el.scrollLeft > 4 && isNearEnd(el)) {
				const distance = Math.max(el.clientWidth * 0.75, 180);
				el.scrollBy({ left: distance, behavior: "smooth" });
				updateScrollState();
			}

			prevLengthRef.current = items.length;
		}, [items.length, isNearEnd, updateScrollState]);

		// Local search changes the visible set — snap back to the start.
		useEffect(() => {
			const el = scrollerRef.current;
			if (!el) return;
			el.scrollLeft = 0;
			updateScrollState();
			// Intentionally only when the search text changes.
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [query]);

		const scrollByPage = (direction) => {
			const el = scrollerRef.current;
			if (!el) return;
			const distance = Math.max(el.clientWidth * 0.75, 180);

			if (direction > 0 && hasMore && !isLoadingMore && !canScrollRight) {
				onLoadMore?.();
				return;
			}

			if (direction > 0 && hasMore && !isLoadingMore && isNearEnd(el)) {
				onLoadMore?.();
			}

			el.scrollBy({ left: direction * distance, behavior: "smooth" });
		};

		const showRight = canScrollRight || hasMore;
		const hasQuery = Boolean(query.trim());
		const emptyText = (() => {
			if (isLoadingMore && visible.length === 0) return loadingMessage;
			if (hasQuery) return noMatchMessage;
			return emptyMessage;
		})();
		const totalLabel =
			typeof totalCount === "number" ? totalCount : hasQuery ? visible.length : items.length;
		const legendCount =
			selected.length > 0 ? `${selected.length} selected of ${totalLabel}` : `${totalLabel}`;

		return (
			<fieldset className="block w-full min-w-0" style={{ minInlineSize: 0 }}>
				<legend className="text-xs font-semibold uppercase tracking-wide text-gray-500">
					{legend}
					<span className="ml-2 font-normal normal-case tracking-normal text-gray-500">
						· {legendCount}
					</span>
				</legend>
				<div className="relative mt-2">
					<SearchGlyph className="left-3" size={15} />
					<input
						type="search"
						value={query}
						onChange={(event) => {
							setQuery(event.target.value);
							onSearchChange?.(event.target.value);
						}}
						onKeyDown={(event) => {
							if (event.key === "Enter") event.preventDefault();
						}}
						placeholder={searchLabel}
						aria-label={searchLabel}
						className="h-10 w-full rounded-full border border-transparent bg-gray-100 pl-9 pr-3 text-sm focus:border-akgreen focus:outline-none focus:ring-2 focus:ring-akgreen"
					/>
				</div>
				<div className="relative mt-3 w-full min-w-0 py-1">
					{visible.length === 0 && !isLoadingMore ? (
						<p className="text-sm text-gray-500">{emptyText}</p>
					) : (
						<>
							{canScrollLeft && (
								<div
									className="pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-white to-transparent"
									aria-hidden="true"
								/>
							)}
							{showRight && (
								<div
									className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-white to-transparent"
									aria-hidden="true"
								/>
							)}
							<div
								ref={scrollerRef}
								onScroll={updateScrollState}
								role="group"
								aria-label={legend}
								className="books-filter-row flex w-full min-w-0 max-w-full gap-2 overflow-x-auto overflow-y-hidden px-11"
							>
								{visible.map((item) => {
									const isSelected = selected.includes(item.name);
									return (
										<button
											key={item.id}
											type="button"
											aria-pressed={isSelected}
											onClick={() => onToggle(item.name)}
											className={`flex-shrink-0 whitespace-nowrap rounded-full border px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-akgreen focus:ring-offset-2 ${
												isSelected
													? "border-akgreenest bg-akgreenest text-white"
													: "border-gray-300 bg-white text-gray-800 hover:border-gray-400"
											}`}
										>
											{item.name}
										</button>
									);
								})}
								{isLoadingMore && (
									<span className="flex-shrink-0 whitespace-nowrap rounded-full border border-dashed border-gray-300 px-3 py-1.5 text-sm text-gray-500">
										Loading…
									</span>
								)}
							</div>
						</>
					)}
					{canScrollLeft && (
						<span className="absolute inset-y-0 left-0 z-10 flex items-center">
							<button
								type="button"
								onClick={() => scrollByPage(-1)}
								aria-label={`Show previous ${legend.toLowerCase()}`}
								className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-akgreenest shadow-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-akgreen"
							>
								<FiChevronLeft className="block" size={22} />
							</button>
						</span>
					)}
					{showRight && (visible.length > 0 || isLoadingMore) && (
						<span className="absolute inset-y-0 right-0 z-10 flex items-center">
							<button
								type="button"
								onClick={() => scrollByPage(1)}
								disabled={isLoadingMore && !canScrollRight}
								aria-label={`Show more ${legend.toLowerCase()}`}
								className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-akgreenest shadow-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-akgreen disabled:opacity-50"
							>
								<FiChevronRight className="block" size={22} />
							</button>
						</span>
					)}
				</div>
			</fieldset>
		);
	},
);

const BooksFilter = () => {
	const history = useHistory();
	const location = useLocation();
	const prefersReducedMotion = useReducedMotion();
	const [tagSearch, setTagSearch] = useState("");
	const initialDraftRef = useRef(withoutPageAndLanguage(draftFromSearch(location.search)));
	const [draft, setDraft] = useState(() => initialDraftRef.current);
	const returnPath = booksResultsPathFromSearch(location.search);
	const selectedCount = countActiveFilters(draft);
	const isDirty = useMemo(
		() => JSON.stringify(draft) !== JSON.stringify(initialDraftRef.current),
		[draft],
	);

	const { data: categories, isLoading: isLoadingCategories } = useFetchCategories();
	const { data: publishers, isLoading: isLoadingPublishers } = useFetchPublishers();
	const { data: genres, isLoading: isLoadingGenres } = useGenres();
	const pagedTags = usePagedTags({ search: tagSearch, rowsPerPage: 40, sort: "usage" });

	const categoryOptions = useMemo(
		() =>
			(categories?.data || []).map((category) => ({ value: category.name, label: category.name })),
		[categories],
	);
	const publisherOptions = useMemo(
		() =>
			(publishers?.data || []).map((publisher) => ({
				value: publisher.id?.toString(),
				label: publisher.name,
			})),
		[publishers],
	);
	const countryOptions = useMemo(
		() => countries.map((country) => ({ value: country.name, label: country.name })),
		[],
	);
	const genreList = genres?.data?.data || [];
	const isLoadingOptions = isLoadingCategories || isLoadingPublishers || isLoadingGenres;

	const setField = useCallback((key, value) => {
		setDraft((current) => ({ ...current, [key]: value }));
	}, []);

	const toggleName = useCallback((key, name) => {
		setDraft((current) => {
			const selected = current[key];
			const next = selected.includes(name)
				? selected.filter((item) => item !== name)
				: [...selected, name];
			return { ...current, [key]: next };
		});
	}, []);

	const onToggleGenre = useCallback((name) => toggleName("genreIs", name), [toggleName]);
	const onToggleTag = useCallback((name) => toggleName("tagIs", name), [toggleName]);

	const warnings = [
		dateWarning(draft.publicationDateFrom, draft.publicationDateTo),
		rangeWarning(draft.priceMin, draft.priceMax, "Price"),
	].filter(Boolean);

	const handleSubmit = (event) => {
		event.preventDefault();
		history.push(appliedBooksPath(withoutPageAndLanguage(draft)));
	};

	const handleClear = () => {
		setDraft(emptyDraft());
	};

	const handleCancel = useCallback(() => {
		history.push(returnPath);
	}, [history, returnPath]);

	const handleBackdropClick = () => {
		if (!isDirty) handleCancel();
	};

	useEffect(() => {
		const onKeyDown = (event) => {
			if (event.key !== "Escape") return;
			const target = event.target;
			const tag = target?.tagName;
			if (
				(tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") &&
				String(target.value || "") !== ""
			) {
				return;
			}
			handleCancel();
		};
		document.addEventListener("keydown", onKeyDown);
		return () => document.removeEventListener("keydown", onKeyDown);
	}, [handleCancel]);

	const selectFields = [
		{
			id: "filter-publisher",
			label: "Publisher",
			fieldKey: "publisherIs",
			value: draft.publisherIs,
			placeholder: "All publishers",
			options: publisherOptions,
		},
		{
			id: "filter-category",
			label: "Category",
			fieldKey: "categoryIs",
			value: draft.categoryIs,
			placeholder: "All categories",
			options: categoryOptions,
		},
		{
			id: "filter-country",
			label: "Country",
			fieldKey: "countryIs",
			value: draft.countryIs,
			placeholder: "All countries",
			options: countryOptions,
		},
		{
			id: "filter-pricing",
			label: "Pricing",
			fieldKey: "pricingModelIs",
			value: draft.pricingModelIs,
			placeholder: "Any pricing",
			options: PRICING_OPTIONS,
		},
		{
			id: "filter-format",
			label: "Format",
			fieldKey: "format",
			value: draft.format,
			placeholder: "Any format",
			options: FORMAT_OPTIONS,
		},
		{
			id: "filter-availability",
			label: "Availability",
			fieldKey: "availability",
			value: draft.availability,
			placeholder: "Any availability",
			options: AVAILABILITY_OPTIONS,
		},
	];

	return (
		<div className="catalogue-filter-page relative min-h-screen overflow-x-hidden font-sans text-sm text-akgreenest">
			<motion.div
				className="absolute inset-0"
				onClick={handleBackdropClick}
				aria-hidden="true"
				initial={prefersReducedMotion ? false : { opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ duration: 0.65, ease: "easeOut" }}
			>
			</motion.div>
			<div className="pointer-events-none relative z-10 flex min-h-screen w-full min-w-0 flex-col items-center justify-center overflow-x-hidden p-4 sm:p-8">
				<motion.div
					className="catalogue-filter-surface pointer-events-auto relative flex w-full min-w-0 max-w-3xl flex-col overflow-hidden bg-white"
					style={{
						maxHeight: "calc(100vh - 2rem)",
					}}
					initial={
						prefersReducedMotion
							? false
							: {
									opacity: 0,
									y: -28,
									scale: 0.97,
								}
					}
					animate={{ opacity: 1, y: 0, scale: 1 }}
					transition={
						prefersReducedMotion
							? { duration: 0 }
							: {
									duration: 0.18,
									delay: 0,
									ease: [0.22, 1, 0.36, 1],
								}
					}
				>
					<motion.div
						{...stackedEntrance(prefersReducedMotion, 0.38, 8)}
						className="flex flex-shrink-0 items-start justify-between gap-3  px-5 py-4 sm:px-6"
					>
						<div className="min-w-0 pr-2">
							<p className="text-xs font-semibold uppercase tracking-wide text-akgreener">
								Akello Library
							</p>
							<h1 className="mt-0.5 text-lg font-semibold text-akgreenest">
								Search and filter books
							</h1>
							<p className="mt-1 text-sm text-gray-500">
								Narrow the catalogue to find the books you want.
							</p>
						</div>
						<button
							type="button"
							onClick={handleCancel}
							aria-label="Close"
							className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-akgreenest focus:outline-none focus:ring-2 focus:ring-akgreen"
						>
							<IoClose size={20} />
						</button>
					</motion.div>
					<form onSubmit={handleSubmit} className="flex min-h-0 min-w-0 flex-1 flex-col">
						<motion.div
							{...stackedEntrance(prefersReducedMotion, 0.52)}
							className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto px-5 pb-5 pt-1 sm:px-6"
						>
							<div className="relative">
								<SearchGlyph />
								<label htmlFor="filter-search" className="sr-only">
									Search
								</label>
								<input
									id="filter-search"
									type="search"
									value={draft.searchTerm}
									onChange={(event) => setField("searchTerm", event.target.value)}
									placeholder="Search keywords"
									autoComplete="off"
									autoFocus
									className="h-12 w-full rounded-full border border-transparent bg-gray-100 pl-11 pr-4 text-sm text-gray-900 placeholder-gray-400 focus:border-akgreen focus:outline-none focus:ring-2 focus:ring-akgreen"
								/>
							</div>

							{isLoadingOptions && (
								<p className="mt-4 text-sm text-gray-500">Loading filter options…</p>
							)}

							<div className="catalogue-sheet-selects mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
								{selectFields.map((field, index) => (
									<div
										key={field.id}
										className={`border-gray-100 ${index % 2 === 0 ? "sm:border-r" : ""} ${
											index % 3 === 2 ? "lg:border-r-0" : "lg:border-r"
										}`}
									>
										<SheetSelect {...field} onChange={setField} />
									</div>
								))}
							</div>

							<div className="mt-5 space-y-4">
								<div>
									<p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
										Published between
									</p>
									<div className="grid grid-cols-2 gap-3">
										<PillInput
											id="filter-published-from"
											label="Published from"
											fieldKey="publicationDateFrom"
											type="date"
											value={draft.publicationDateFrom}
											onChange={setField}
										/>
										<PillInput
											id="filter-published-to"
											label="Published to"
											fieldKey="publicationDateTo"
											type="date"
											value={draft.publicationDateTo}
											onChange={setField}
										/>
									</div>
								</div>
								<div>
									<p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
										Price, USD
									</p>
									<div className="grid grid-cols-2 gap-3">
										<PillInput
											id="filter-price-min"
											label="Minimum price (USD)"
											fieldKey="priceMin"
											type="number"
											min="0"
											step="0.01"
											inputMode="decimal"
											value={draft.priceMin}
											onChange={setField}
											placeholder="Min price"
										/>
										<PillInput
											id="filter-price-max"
											label="Maximum price (USD)"
											fieldKey="priceMax"
											type="number"
											min="0"
											step="0.01"
											inputMode="decimal"
											value={draft.priceMax}
											onChange={setField}
											placeholder="Max price"
										/>
									</div>
								</div>
								{warnings.length > 0 && (
									<ul className="space-y-1 text-sm text-gray-600">
										{warnings.map((warning) => (
											<li key={warning}>{warning}</li>
										))}
									</ul>
								)}
							</div>

							<div className="mt-6 space-y-5">
								<PillChoices
									legend="Collections"
									items={genreList}
									selected={draft.genreIs}
									onToggle={onToggleGenre}
									searchLabel="Search collections"
									emptyMessage="No collections yet"
									noMatchMessage="No collections match your search"
									totalCount={genreList.length}
								/>
								<PillChoices
									legend="Tags"
									items={pagedTags.records}
									selected={draft.tagIs}
									onToggle={onToggleTag}
									searchLabel="Search tags"
									emptyMessage="No tags yet"
									loadingMessage="Loading tags…"
									noMatchMessage="No tags match your search"
									remote
									onSearchChange={setTagSearch}
									hasMore={pagedTags.hasMorePages}
									onLoadMore={pagedTags.loadMore}
									isLoadingMore={
										pagedTags.isLoadingMore ||
										(pagedTags.isFetching && pagedTags.records.length === 0)
									}
									totalCount={pagedTags.totalRecords}
								/>
							</div>
						</motion.div>

						<motion.div
							{...stackedEntrance(prefersReducedMotion, 0.68, 8)}
							className="flex flex-shrink-0 flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-5 py-4 sm:px-6"
						>
							<div className="flex flex-wrap items-center gap-3">
								<p role="status" className="text-xs text-gray-500">
									{selectedCount} {selectedCount === 1 ? "filter selected" : "filters selected"}
								</p>
								<button
									type="button"
									onClick={handleClear}
									className="h-11 px-2 text-sm font-medium text-gray-500 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-akgreen"
								>
									Clear
								</button>
							</div>
							<div className="flex flex-wrap items-center gap-3">
								<button
									type="button"
									onClick={handleCancel}
									className="inline-flex h-11 items-center gap-2 rounded-full border-2 border-akgreenest px-5 text-sm font-semibold text-akgreenest hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-akgreen focus:ring-offset-2"
								>
									<IoClose size={16} />
									Cancel
								</button>
								<button
									type="submit"
									className="h-11 rounded-full bg-akgreenest px-8 text-sm font-semibold text-white hover:bg-akgreener focus:outline-none focus:ring-2 focus:ring-akgreen focus:ring-offset-2"
								>
									Show books
								</button>
							</div>
						</motion.div>
					</form>
				</motion.div>
			</div>
		</div>
	);
};

export default BooksFilter;
